/**
 * Calcula o nível com base no XP acumulado.
 * A fórmula para o XP necessário para alcançar o nível N+1 é 100 * ((N+1)^1.8).
 */
export function calculateLevel(totalXP: number): number {
  if (totalXP < 0) return 1; // XP não pode ser negativo, nível mínimo é 1

  let level = 1;
  while (true) {
    // Calcula o XP necessário para atingir o próximo nível (level + 1)
    const xpNeededForNextLevel = 100 * Math.pow(level + 1, 1.8);

    if (totalXP < xpNeededForNextLevel) {
      // Se o XP total do usuário é menor que o necessário para o próximo nível,
      // então 'level' é o nível atual do usuário.
      break;
    }
    // Caso contrário, o usuário tem XP suficiente para este 'level', então incrementamos
    // para verificar o próximo.
    level++;
  }
  return level;
}

/**
 * Calcula o XP necessário para atingir um determinado nível.
 */
export function getXPForLevel(level: number): number {
  if (level <= 1) return 0;
  return 100 * Math.pow(level, 1.8);
}

/**
 * Calcula o XP necessário para o próximo nível a partir do nível atual.
 */
export function getXPForNextLevel(currentLevel: number): number {
  return getXPForLevel(currentLevel + 1);
}

/**
 * Calcula o XP necessário para atingir o próximo nível.
 * @param currentLevel - O nível atual do usuário.
 * @returns O XP total necessário para atingir o nível seguinte.
 */
export function calculateNextLevelXP(currentLevel: number): number {
  // XP necessário para o nível (currentLevel + 1)
  return Math.floor(100 * Math.pow(currentLevel + 1, 1.8));
}

/**
 * Calcula o progresso percentual em direção ao próximo nível.
 * @param currentXP - O XP atual do usuário.
 * @param currentLevel - O nível atual do usuário.
 * @returns A porcentagem de progresso (0-100).
 */
export function calculateLevelProgress(currentXP: number, currentLevel: number): number {
  // XP necessário para o nível atual (base do progresso)
  const xpForCurrentLevel = currentLevel <= 1 ? 0 : Math.floor(100 * Math.pow(currentLevel, 1.8));
  
  // XP necessário para o próximo nível (teto do progresso)
  const xpForNextLevel = calculateNextLevelXP(currentLevel);

  const totalXpNeededForLevel = xpForNextLevel - xpForCurrentLevel;
  const xpProgressInLevel = currentXP - xpForCurrentLevel;

  if (totalXpNeededForLevel <= 0) {
    return 100; // Evita divisão por zero se já estiver no nível máximo ou erro
  }

  const progress = Math.floor((xpProgressInLevel / totalXpNeededForLevel) * 100);

  // Garante que o progresso esteja entre 0 e 100
  return Math.max(0, Math.min(100, progress));
} 